import { Component, EventEmitter, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HeaderService } from '../../core/services/header.service';
import { Title } from '@angular/platform-browser';
import { BaseComponent } from '../../core/base.component';
import { AuthService } from '../../core/security/auth.service';
import { RolesEnum } from '../../core/security/roles.enum';
import { LocalFaxService } from '../local-fax.service';
import { CurrentUser } from '../../api/models/current-user';
import { FaxListFilterDialogComponent } from '../fax-list-filter/fax-list-filter-dialog.component';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { ModulesEnum } from '../../core/security/modules.enum';
import { SelectedFilterValuesModel } from '../../dialogs/grid-filter/selected-filter-values.model';
import { FilterOptionModel } from '../../dialogs/grid-filter/filter-option.model';
import { MatDialog } from '@angular/material/dialog';
import { MatCard, MatCardHeader, MatCardContent } from '@angular/material/card';
import { MessagesModule } from 'primeng/messages';
import { FlexModule } from '@angular/flex-layout';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { FilterRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-row-additional-content.component';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { FaxGridComponent } from '../grid/fax-grid.component';
import { Message } from 'primeng/message';
import { HelpscoutService } from '../../core/services/helpscout.service';

@Component({
  selector: 'app-fax-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  imports: [
    MatCard,
    MatCardHeader,
    MatCardContent,
    MessagesModule,
    RouterLink,
    FlexModule,
    GridFilterComponent,
    FilterRowAdditionalContentComponent,
    MatButton,
    MatTooltip,
    MatIcon,
    FaxGridComponent,
    Message,
  ],
})
export class FaxListComponent extends BaseComponent implements OnInit, OnDestroy {
  filterChangeEvent = new EventEmitter<any>();
  selectedFilterChange = new EventEmitter<SelectedFilterValuesModel[]>();

  selectedFilter: SelectedFilterValuesModel[] = new Array<SelectedFilterValuesModel>();
  filterOptions: FilterOptionModel[] = [];
  faxNumbers: any[] = [];

  userRights = {
    previewFax: false,
    downloadFax: false,
    deleteFax: false,
  };
  hasContactsModule = false;
  hasContactsEditRight = false;
  hasPhoneNumbersEditRight = false;

  private paramsSubscription;
  private filterFaxId: string;

  constructor(
    route: ActivatedRoute,
    headerService: HeaderService,
    titleService: Title,
    public authService: AuthService,
    public faxService: LocalFaxService,
    public supportService: HelpscoutService,
    public dialog: MatDialog,
    private router: Router,
    public zone: NgZone,
  ) {
    super(route, headerService, titleService, authService);

    this.editRoles = [RolesEnum.Fax_Edit, RolesEnum.Fax_Delete];
    this.deleteRoles = [RolesEnum.Fax_Delete];
    this.headerService.hideSearchInput();

    this.paramsSubscription = this.route.queryParams.subscribe(params => {
      const faxId = params['faxId']; // --> Name must match wanted parameter
      console.log(`new url param: ${faxId}`);
      this.filterFaxId = faxId;
    });
  }

  onCurrentUserChanged(user: CurrentUser): void {
    if (user) {
      // update user rights
      this.userRights.previewFax = !!user.roles.find(role => {
        return role === RolesEnum.Fax_View || role === RolesEnum.Fax_Edit || role === RolesEnum.Fax_Delete;
      });
      this.userRights.downloadFax = !!user.roles.find(role => {
        return role === RolesEnum.Fax_Edit || role === RolesEnum.Fax_Delete;
      });
      this.userRights.deleteFax = !!user.roles.find(role => role === RolesEnum.Fax_Delete);

      this.hasContactsModule = this.authService.userHasPackage([
        ModulesEnum.ContactModule,
        ModulesEnum.PrivateContactModule,
      ]);
      this.hasContactsEditRight = this.authService.hasAccess([
        RolesEnum.CompanyContact_Edit,
        RolesEnum.CompanyContact_Delete,
        RolesEnum.PrivateContact_Edit,
        RolesEnum.PrivateContact_Delete,
      ]);
      this.hasPhoneNumbersEditRight = this.authService.hasAccess([
        RolesEnum.PhoneNumbers_Edit,
        RolesEnum.PhoneNumbers_Delete,
      ]);
    } else {
      this.userRights = {
        previewFax: false,
        downloadFax: false,
        deleteFax: false,
      };

      this.hasContactsModule = false;
      this.hasContactsEditRight = false;
      this.hasPhoneNumbersEditRight = false;
    }
  }

  async ngOnInit() {
    await super.ngOnInit();

    this.faxService.faxAllNumbersList().then(faxNumbers => {
      this.faxNumbers = faxNumbers.map(x => {
        return {
          id: x.id,
          value: x.id,
          text: x.text,
        };
      });
      this.initFilterOptions();
    });
  }

  async openSendFaxView() {
    const elm = document.querySelector('.send-fax-button.stop-click-action');
    if (!elm) {
      //do not navigate if this is mobile app
      await this.router.navigate([this.headerService.themePrefix, 'fax', 'send-fax']);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.headerService.hideSearchInput();
    this.headerService.searchText.next('');
  }

  onSelectedItemsChanged($event: SelectedFilterValuesModel[]) {
    this.selectedFilter = $event;

    if (this.filterFaxId) {
      this.selectedFilter.push({
        filterKey: 'faxId',
        multiValue: false,
        title: 'Fax Id',
        values: [this.filterFaxId],
        displayValues: [this.filterFaxId],
      });
      this.filterFaxId = '';
      // this.selectedFilterChange.emit(this.selectedFilter);
    }
  }

  onFilterChanged(filter: any) {
    this.filterChangeEvent.emit(filter);
  }

  openSupportWidget() {
    this.supportService.toggle();
  }

  showFilterDialog() {
    console.log('open filter dialog');
    const dialogRef = this.dialog.open(FaxListFilterDialogComponent, {
      width: '600px',
      height: '90%',
      data: {
        filter: this.selectedFilter,
        faxNumbers: this.faxNumbers,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: SelectedFilterValuesModel[]) => {
      if (result) {
        this.selectedFilterChange.emit(result);
      }
    });
  }

  initFilterOptions(): void {
    this.filterOptions = [
      {
        filterKey: 'searchText',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Any',
      },
      {
        filterKey: 'sentBy',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Sent By',
      },
      {
        filterKey: 'numberFrom',
        multiValue: false,
        showAlways: true,
        title: 'From Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'numberTo',
        multiValue: false,
        showAlways: true,
        title: 'To Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'Failed',
        value: 'Failed',
        searchText: 'Status: Failed',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'Processing',
        value: 'Processing',
        searchText: 'Status: Processing',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Status',
        displayValue: 'Success',
        value: 'Success',
        searchText: 'Status: Success',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Type',
        displayValue: 'Incoming',
        value: 'incoming',
        searchText: 'Type: Incoming',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Type',
        displayValue: 'Outgoing',
        value: 'outgoing',
        searchText: 'Type: Outgoing',
      },
      {
        filterKey: 'faxId',
        multiValue: false,
        showAlways: true,
        type: 'number',
        title: 'Fax Id',
      },
    ];

    if (this.faxNumbers?.length > 0) {
      for (const faxNumber of this.faxNumbers) {
        this.filterOptions.push({
          filterKey: 'faxNumbers',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Fax Number',
          displayValue: PhoneNumbersUtils.format(faxNumber.text),
          value: faxNumber.id,
          searchText: `Fax Number: ${PhoneNumbersUtils.format(faxNumber.text)} ${faxNumber.id}`,
        });
      }
    }
  }
}
